import * as React from "react"
import { ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react"

import { cn } from "@/components/utils/utils"
import { ButtonProps, buttonVariants } from "@/components/ui/button"
import { useI18n } from "@/hooks/useI18n"

const Pagination = ({ className, ...props }: React.ComponentProps<"nav">) => (
  <nav
    role="navigation"
    aria-label="pagination"
    className={cn("mx-auto flex w-full justify-center", className)}
    {...props}
  />
)
Pagination.displayName = "Pagination"

const PaginationContent = React.forwardRef<
  HTMLUListElement,
  React.ComponentProps<"ul">
>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    className={cn("flex flex-row items-center gap-1", className)}
    {...props}
  />
))
PaginationContent.displayName = "PaginationContent"

const PaginationItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentProps<"li">
>(({ className, ...props }, ref) => (
  <li ref={ref} className={cn("", className)} {...props} />
))
PaginationItem.displayName = "PaginationItem"

type PaginationLinkProps = {
  isActive?: boolean
} & Pick<ButtonProps, "size"> &
  React.ComponentProps<"a">

const PaginationLink = ({
  className,
  isActive,
  size = "icon",
  ...props
}: PaginationLinkProps) => (
  <a
    aria-current={isActive ? "page" : undefined}
    className={cn(
      buttonVariants({
        variant: isActive ? "outline" : "ghost",
        size,
      }),
      className
    )}
    {...props}
  />
)
PaginationLink.displayName = "PaginationLink"

/**
 * Frecce di pagina senza testo: i numeri accanto dicono gia' dove si va, e un'etichetta
 * ("Precedente", "Prev") allargava il controllo senza aggiungere nulla. Il nome resta
 * per screen reader e tooltip. Tonde come i numeri: stanno nella stessa capsula.
 */
const ARROW_CLASSES =
  "h-8 w-8 rounded-full text-gray-600 hover:bg-gray-100 hover:text-gray-900"

const PaginationPrevious = ({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => {
  const { t } = useI18n()
  const label = String(t("common.pagePrevious"))
  return (
    <PaginationLink
      aria-label={label}
      title={label}
      size="icon"
      className={cn(ARROW_CLASSES, className)}
      {...props}
    >
      <ChevronLeft className="h-4 w-4" strokeWidth={2.5} />
    </PaginationLink>
  )
}
PaginationPrevious.displayName = "PaginationPrevious"

const PaginationNext = ({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => {
  const { t } = useI18n()
  const label = String(t("common.pageNext"))
  return (
    <PaginationLink
      aria-label={label}
      title={label}
      size="icon"
      className={cn(ARROW_CLASSES, className)}
      {...props}
    >
      <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
    </PaginationLink>
  )
}
PaginationNext.displayName = "PaginationNext"

const PaginationEllipsis = ({
  className,
  ...props
}: React.ComponentProps<"span">) => (
  <span
    aria-hidden
    className={cn("flex h-9 w-9 items-center justify-center", className)}
    {...props}
  >
    <MoreHorizontal className="h-4 w-4" />
    <span className="sr-only">More pages</span>
  </span>
)
PaginationEllipsis.displayName = "PaginationEllipsis"

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
}
